import {
  Assignment,
  CalendarMonth,
  Description,
  FileCopy,
  MedicalServices,
  Medication,
} from "@mui/icons-material";
import {
  Box,
  Button,
  ButtonBase,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import type { SvgIconProps } from "@mui/material/SvgIcon";
import { useNavigate } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import { useState } from "react";

import { MobileBottomSheet } from "@/components/common/MobileBottomSheet";
import { ScheduleForDash } from "../../ScheduleForDash";
import { FitnessDataModal } from "../compontents/FitnessDataModal";
import { PatientQuestionnaireTasksSection } from "./PatientQuestionnaireTasksSection";
import { DashMobileSection } from "./DashMobileSection";
import { PatientSupportingDocumentsDashButton } from "./patientSupportingDocuments/PatientSupportingDocumentsDashButton";

type MobileActionId = "exames" | "procedures" | "certificates";

type PaletteAccent =
  | "primary800"
  | "secondary800"
  | "success700"
  | "error700";

interface MobileAction {
  id: MobileActionId | "agendar" | "registros";
  label: string;
  icon: React.ComponentType<SvgIconProps>;
  accent: PaletteAccent;
  /** Atalho direto — sem passar pelo PatientDash. */
  href?: string;
}

/** Linha superior: Agendamento, Procedimentos, Relatórios e Registros (verde nas extremidades). */
const TOP_ACTIONS: MobileAction[] = [
  {
    id: "agendar",
    label: "Agendamento",
    icon: CalendarMonth,
    accent: "primary800",
    href: "/schedulingsearch",
  },
  {
    id: "procedures",
    label: "Procedimentos",
    icon: MedicalServices,
    accent: "error700",
  },
  {
    id: "certificates",
    label: "Relatórios",
    icon: FileCopy,
    accent: "secondary800",
  },
  {
    id: "registros",
    label: "Registros",
    icon: Assignment,
    accent: "primary800",
    href: "/myrecords",
  },
];

interface PatientDashMobileProps {
  userName?: string;
  refreshTokenGoogleFit?: string;
  openAgendaFull: boolean;
  onOpenAgendaFull: (open: boolean) => void;
  onMobileAction: (id: MobileActionId | "medications") => void;
}

function DashSurfaceCard({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        p: { xs: 1, sm: 1.25 },
        boxShadow: "0 2px 12px rgba(15, 23, 42, 0.04)",
      }}
    >
      {children}
    </Box>
  );
}

function DocumentQuickChip({
  action,
  onClick,
}: {
  action: MobileAction;
  onClick: () => void;
}) {
  const theme = useTheme();
  const accent =
    (theme.palette as unknown as Record<string, { main?: string }>)[
      action.accent
    ]?.main ?? theme.palette.primary.main;

  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        width: "100%",
        minWidth: 0,
        borderRadius: 2,
        py: 0.5,
        px: 0.25,
        bgcolor: "transparent",
        WebkitTapHighlightColor: "transparent",
        "&:active": { transform: "scale(0.96)", opacity: 0.88 },
      }}
    >
      <Stack alignItems="center" spacing={0.85} sx={{ width: "100%" }}>
        <Box
          sx={{
            width: { xs: 42, sm: 48 },
            height: { xs: 42, sm: 48 },
            borderRadius: 2,
            bgcolor: accent,
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <action.icon sx={{ fontSize: { xs: 22, sm: 26 } }} />
        </Box>
        <Typography
          component="span"
          variant="caption"
          sx={{
            display: "block",
            fontWeight: 600,
            fontSize: "clamp(0.55rem, 2.1vw, 0.72rem)",
            lineHeight: 1.15,
            textAlign: "center",
            width: "100%",
            px: 0.15,
            whiteSpace: "pre-line",
            wordBreak: "normal",
            overflowWrap: "normal",
            hyphens: "none",
            color: "text.primary",
          }}
        >
          {action.label}
        </Typography>
      </Stack>
    </ButtonBase>
  );
}

const agendaSectionSx = { mt: 2 };

/** Compensa o padding duplicado do layout mobile acima da barra de navegação. */
const MOBILE_NAV_OFFSET =
  "calc(-96px - env(safe-area-inset-bottom, 0px))";

export function PatientDashMobile({
  refreshTokenGoogleFit = "",
  openAgendaFull,
  onOpenAgendaFull,
  onMobileAction,
}: PatientDashMobileProps) {
  const navigate = useNavigate();
  const theme = useTheme();
  /** Mês compartilhado entre o preview da agenda e o modal Ver tudo. */
  const [agendaMonth, setAgendaMonth] = useState<Dayjs>(() => dayjs());

  const scheduleDashProps = {
    compact: true as const,
    listLayout: "slider" as const,
    hideNewAppointmentButton: true,
    dateFilter: agendaMonth,
    onDateFilterChange: setAgendaMonth,
  };

  return (
    <>
      <Box
        sx={{
          px: 1.5,
          pt: 0.5,
          pb: 0,
          mb: MOBILE_NAV_OFFSET,
        }}
      >

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
              gap: 0.75,
              width: "100%",
              mt: 2,
            }}
          >
            {TOP_ACTIONS.map((action) => (
              <DocumentQuickChip
                key={action.id}
                action={action}
                onClick={() =>
                  action.href
                    ? navigate(action.href)
                    : onMobileAction(action.id as MobileActionId)
                }
              />
            ))}
          </Box>

        <Box sx={{ mt: 2 }}>
          <PatientSupportingDocumentsDashButton />
        </Box>

        <Stack direction="row" spacing={1.25} sx={{ mt: 2.25 }}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<Description />}
            onClick={() => onMobileAction("exames")}
            sx={{
              borderRadius: 99,
              py: 1.15,
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.8125rem",
              bgcolor: alpha(theme.palette.grey[500], 0.08),
              borderColor: "primary800.main",
              borderWidth: 1.5,
              color: "text.primary",
              "& .MuiButton-startIcon": { color: "primary800.main" },
            }}
          >
            Exames
          </Button>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<Medication />}
            onClick={() => onMobileAction("medications")}
            sx={{
              borderRadius: 99,
              py: 1.15,
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.8125rem",
              bgcolor: alpha(theme.palette.grey[500], 0.08),
              borderColor: "primary800.main",
              borderWidth: 1.5,
              color: "text.primary",
              "& .MuiButton-startIcon": { color: "primary800.main" },
            }}
          >
            Medicamentos
          </Button>
        </Stack>

        {/* Agenda — sempre visível; vazio exibe mensagem no lugar do card */}
        <Box sx={agendaSectionSx}>
          <ScheduleForDash
            {...scheduleDashProps}
            dashboardPreview
            onViewAll={() => onOpenAgendaFull(true)}
          />
        </Box>

        <DashMobileSection
          title="Questionários"
          subtitle="Responda pendências da equipe"
          spacingTop={2}
        >
          <PatientQuestionnaireTasksSection
            variant="tutor"
            compact
            compactTabsOnly
            defaultTasksTab="open"
            fullListTodosOnly
          />
        </DashMobileSection>

        <DashMobileSection
          title="Dados fitness"
          subtitle="Google Health e métricas de saúde"
          spacingTop={2}
        >
          <DashSurfaceCard>
            <FitnessDataModal
              embedded
              active
              refreshToken={refreshTokenGoogleFit}
            />
          </DashSurfaceCard>
        </DashMobileSection>
      </Box>

      <MobileBottomSheet
        open={openAgendaFull}
        onClose={() => onOpenAgendaFull(false)}
        title="Próximas consultas"
        subtitle="Suas consultas do mês"
      >
        <Box sx={{ py: 1, px: 2 }}>
          <ScheduleForDash
            compact
            listLayout="vertical"
            hideNewAppointmentButton
            useDashboardMonthControls
            dateFilter={agendaMonth}
            onDateFilterChange={setAgendaMonth}
          />
        </Box>
      </MobileBottomSheet>
    </>
  );
}
